import {
  BoltIcon,
  RocketLaunchIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { CalendarIcon, CheckIcon, SquareIcon } from "@radix-ui/react-icons";
import clsx from "clsx";
import dayjs from "dayjs";
import parse from "parse-duration";
import React, { useEffect, useState } from "react";
import {
  ProjectData,
  useGetProjects,
} from "src/api/Services/Projects/projects";
import { TaskData, useGetPhaseTasks } from "src/api/Services/Tasks/tasks";
import { useCreateTimeEntry } from "src/api/timesheets/timesheets";
import { Button } from "src/components/ui/button";
import { Calendar } from "src/components/ui/calendar";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "src/components/ui/command";
import { Dialog, DialogContent } from "src/components/ui/dialog";
import { Loading } from "src/components/ui/loading";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "src/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/ui/select";
import { Switch } from "src/components/ui/switch";
import { TextInput } from "src/components/ui/text-input";
import { useRealmApp } from "src/store/RealmApp";
import { convertMsToNatural } from "../timesheets/timesheets";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export default function NewEntryDialog({ open, onOpenChange }: Props) {
  return (
    <NewEntryContextProvider>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl">
          <div className="space-y-4">
            <div className="text-sm font-light leading-none text-gray-600">
              New Time Entry
            </div>
            <div className="space-y-4">
              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <ProjectAndStageCombobox />
                  <TaskComboboxWrapper />
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <DatePicker />
                  <TimeInput />
                  <BillableSelect />
                </div>
              </div>
              <DescriptionTextarea />
            </div>
            <div className="flex justify-end gap-2">
              <CreateMoreSwitch />
              <Button variant="secondary" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <CreateButton onOpenChange={onOpenChange} />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </NewEntryContextProvider>
  );
}

function CreateMoreSwitch() {
  const newEntryCtx = useNewEntry();
  return (
    <div className="flex items-center space-x-1.5">
      <Switch
        checked={newEntryCtx.state.isCreateMore}
        onCheckedChange={(value) => {
          newEntryCtx.dispatch({
            type: "IS_CREATE_MORE_UPDATE",
            isCreateMore: !!value,
          });
        }}
      />
      <div className="text-xs text-gray-400">Create more</div>
    </div>
  );
}

function DatePicker() {
  const newEntryCtx = useNewEntry();
  const [open, setOpen] = useState(false);
  return (
    <Popover
      open={open}
      onOpenChange={(open) => {
        setOpen(open);
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant={"secondary"}
          className={clsx(
            "h-8 justify-between text-left font-normal",
            !newEntryCtx.state.date && "text-muted-foreground"
          )}
        >
          <div className="inline-flex items-center">
            <CalendarIcon className="mr-2 h-3.5 w-3.5" />
            {newEntryCtx.state.date ? (
              dayjs(newEntryCtx.state.date).format("ddd, DD MMM")
            ) : (
              <div className="flex space-x-1 text-gray-400/60">
                Date
                <span className="top-0 ml-0.5 text-primary-main">*</span>
              </div>
            )}
          </div>
          {/* <CaretSortIcon className="ml-2 h-4 w-4 opacity-50" /> */}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        animateOut={false}
        className="w-[248px] p-0"
        align="start"
      >
        <Calendar
          mode="single"
          selected={
            newEntryCtx.state.date
              ? new Date(newEntryCtx.state.date)
              : undefined
          }
          onSelect={(date) => {
            if (date) {
              // const defaultTime = new Date(date);
              // defaultTime.setHours(23, 30, 0, 0);
              newEntryCtx.dispatch({
                type: "DATE_UPDATE",
                date: dayjs(date).format("YYYY-MM-DD"),
              });
              setOpen(false);
            }
          }}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

function CreateButton({
  onOpenChange,
}: {
  onOpenChange: (open: boolean) => void;
}) {
  const app = useRealmApp();
  const newEntryCtx = useNewEntry();
  const createTimeEntry = useCreateTimeEntry();

  useEffect(() => {
    if (createTimeEntry.isSuccess) {
      if (!newEntryCtx.state.isCreateMore) {
        onOpenChange(false);
        newEntryCtx.dispatch({
          type: "PROJECT_UPDATE",
          project: undefined,
        });
      }
      newEntryCtx.dispatch({
        type: "STAGE_UPDATE",
        stage: undefined,
      });
      newEntryCtx.dispatch({
        type: "TASK_UPDATE",
        task: undefined,
      });
      newEntryCtx.dispatch({
        type: "DESCRIPTION_UPDATE",
        description: "",
      });
      newEntryCtx.dispatch({
        type: "TIME_UPDATE",
        time: null,
      });
      // newEntryCtx.dispatch({
      //   type: "DATE_UPDATE",
      //   date: "",
      // });
      newEntryCtx.dispatch({
        type: "BILLABLE_UPDATE",
        isBillable: true,
      });
    }
  }, [createTimeEntry.isSuccess]);
  return (
    <Button
      disabled={
        !newEntryCtx.state.project?._id ||
        !newEntryCtx.state.time ||
        !newEntryCtx.state.date ||
        createTimeEntry.isLoading
      }
      onClick={() => {
        if (newEntryCtx.state.time && newEntryCtx.state.project?._id) {
          createTimeEntry.mutate({
            date: newEntryCtx.state.date,
            time: newEntryCtx.state.time,
            isBillable: newEntryCtx.state.isBillable,
            description: newEntryCtx.state.description,
            projectId: newEntryCtx.state.project?._id,
            stageId: newEntryCtx.state.stage?._id,
            taskId: newEntryCtx.state.task?._id,
            userId: app.currentUser?.customData._id,
          });
        }
      }}
    >
      {createTimeEntry.isLoading ? "Creating..." : "Create"}
    </Button>
  );
}

function DescriptionTextarea() {
  const newEntryCtx = useNewEntry();
  return (
    <textarea
      placeholder="Description..."
      className="w-full resize-none rounded-md border-0 bg-gray-50 p-3 text-sm transition-colors placeholder:text-gray-400 focus:outline-none focus:ring-0"
      rows={3}
      value={newEntryCtx.state.description}
      onChange={(e) => {
        newEntryCtx.dispatch({
          type: "DESCRIPTION_UPDATE",
          description: e.target.value,
        });
      }}
    />
  );
}

function TimeInput() {
  const newEntryCtx = useNewEntry();
  const [value, setValue] = useState<string>(
    !newEntryCtx.state.time
      ? ""
      : convertMsToNatural(newEntryCtx.state.time || 0)
  );

  useEffect(() => {
    setValue(
      !newEntryCtx.state.time
        ? ""
        : convertMsToNatural(newEntryCtx.state.time || 0)
    );
  }, [newEntryCtx.state.time]);

  return (
    <TextInput
      placeholder="e.g. 2h 30m"
      value={value}
      onChange={(e) => {
        setValue(e.target.value);
      }}
      className={clsx(
        !parse(value) &&
          value !== "" &&
          "border-red-500 ring-[0.5px] ring-red-500",
        "h-8 w-40"
      )}
      onBlur={(e) => {
        newEntryCtx.dispatch({
          type: "TIME_UPDATE",
          time: parse(e.target.value) || 0,
        });
      }}
    />
  );
}

function BillableSelect() {
  const newEntryCtx = useNewEntry();
  return (
    <Select
      onValueChange={(value) => {
        newEntryCtx.dispatch({
          type: "BILLABLE_UPDATE",
          isBillable: value === "true",
        });
      }}
      value={newEntryCtx.state.isBillable ? "true" : "false"}
    >
      <SelectTrigger className="w-32">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="true">
          <div className="flex items-center gap-2">
            {/* <span>$</span> */}
            <span>Billable</span>
          </div>
        </SelectItem>
        <SelectItem value="false">
          <div className="flex items-center gap-2">
            <div>Non-Billable</div>
          </div>
        </SelectItem>
      </SelectContent>
    </Select>
  );
}

function TaskComboboxWrapper() {
  const newEntryCtx = useNewEntry();
  if (!newEntryCtx.state.project?._id) {
    return null;
  }
  return <TaskCombobox />;
}

function TaskCombobox() {
  const newEntryCtx = useNewEntry();
  const { data: tasks } = useGetPhaseTasks(
    String(newEntryCtx.state.project?._id)
  );
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen} modal={true}>
      <PopoverTrigger asChild>
        <Button
          variant="secondary"
          role="combobox"
          aria-expanded={open}
          className={clsx(
            "group relative min-w-[120px] justify-start truncate font-normal"
          )}
        >
          <BoltIcon
            className={clsx(
              "3.5 mr-1.5 w-3.5 shrink-0 text-gray-600"
              // newEntryCtx.state.project ? "text-gray-600" : "text-gray-400"
            )}
          />
          {newEntryCtx.state.task ? (
            <div className="max-w-[160px] truncate">
              {newEntryCtx.state.task.title}
            </div>
          ) : (
            <div className="text-gray-400/60">Task</div>
          )}
          {/* {!!selectedProject && (
        <div className="group absolute right-0 h-full w-20 bg-gradient-to-r from-transparent via-gray-50/70 to-gray-100 opacity-0 transition-all group-hover:opacity-100">
          <div
            className="group absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-gray-200 p-[3px] opacity-0 transition-all group-hover:opacity-100"
            onClick={(e) => {
              e.stopPropagation();
              onProjectDelete();
            }}
          >
            <XMarkIcon className="h-2.5 w-2.5 text-gray-400 group-hover:text-gray-600" />
          </div>
        </div>
      )} */}
          {/* <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" /> */}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        animateOut={false}
        className="pointer-events-auto max-h-[50vh] w-60 p-0"
        align="start"
      >
        {!!tasks ? (
          <>
            {tasks?.length > 0 ? (
              <Command
              // filter={(value, search) => {
              //   if (
              //     tasks
              //       ?.find((t) => t._id === value)
              //       ?.title.toLowerCase()
              //       .includes(search.toLowerCase())
              //   ) {
              //     return 1;
              //   } else {
              //     return 0;
              //   }
              // }}
              >
                <CommandInput placeholder="Search task..." className="h-9" />
                <CommandList>
                  <CommandEmpty>No task found.</CommandEmpty>
                  {newEntryCtx.state.project?.stages
                    .filter((s) => {
                      if (!!newEntryCtx.state.stage?._id) {
                        if (newEntryCtx.state.stage._id === s._id) {
                          return true;
                        }
                        return false;
                      } else {
                        if (s.isUnassigned) {
                          const unassignedTasks = tasks.filter(
                            (t) => t.journeyStage?._id === s._id
                          );
                          if (unassignedTasks.length > 0) {
                            return true;
                          } else {
                            return false;
                          }
                        } else {
                          return true;
                        }
                      }
                    })
                    .map((s) => (
                      <CommandGroup heading={s.name}>
                        {tasks
                          ?.filter((t) => t.journeyStage?._id === s._id)
                          .map((task) => (
                            <CommandItem
                              key={task._id}
                              value={task.title}
                              onSelect={() => {
                                newEntryCtx.dispatch({
                                  type: "TASK_UPDATE",
                                  task,
                                });
                                if (task.journeyStage?._id) {
                                  const stage =
                                    newEntryCtx.state.project?.stages.find(
                                      (s) => s._id === task.journeyStage?._id
                                    );
                                  if (stage) {
                                    newEntryCtx.dispatch({
                                      type: "STAGE_UPDATE",
                                      stage,
                                    });
                                  }
                                }
                                setOpen(false);
                              }}
                              className="gap-2"
                            >
                              <div
                                className={clsx(
                                  "h-2 w-2 shrink-0 rounded-full",
                                  task.status === "To-do" && "bg-amber-300",
                                  task.status === "In Progress" &&
                                    "bg-blue-400",
                                  task.status === "Completed" && "bg-green-400",
                                  task.status === "Canceled" && "bg-gray-400",
                                  task.status === "Stuck" && "bg-red-400",
                                  task.status === "Backlog" && "bg-gray-200"
                                )}
                              />
                              <span className="truncate">{task.title}</span>
                              <div className="flex flex-1 shrink-0 items-center justify-end space-x-1">
                                {newEntryCtx.state.task?._id === task._id ? (
                                  <div
                                    className="cursor-pointer rounded-full p-1 transition-all hover:bg-gray-200"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      newEntryCtx.dispatch({
                                        type: "TASK_UPDATE",
                                        task: undefined,
                                      });
                                      setOpen(false);
                                    }}
                                  >
                                    <XMarkIcon
                                      className={clsx(
                                        "h-2.5 w-2.5 shrink-0",
                                        newEntryCtx.state.task?._id === task._id
                                          ? "opacity-100"
                                          : "opacity-0"
                                      )}
                                    />
                                  </div>
                                ) : (
                                  <div className="w-5" />
                                )}
                                <CheckIcon
                                  className={clsx(
                                    "h-4 w-4 shrink-0",
                                    newEntryCtx.state.task?._id === task._id
                                      ? "opacity-100"
                                      : "opacity-0"
                                  )}
                                />
                              </div>
                            </CommandItem>
                          ))}
                      </CommandGroup>
                    ))}
                </CommandList>
              </Command>
            ) : (
              <div className="px-4 py-2.5 text-sm">
                <div className="select-none">No Tasks Created</div>
                {/* <div className="select-none pt-0.5 text-xs text-gray-500">
              Click{" "}
              <span className="cursor-pointer text-gray-700 hover:text-gray-800">
                here
              </span>{" "}
              to create a project
            </div> */}
              </div>
            )}
          </>
        ) : (
          <Loading className="mx-auto my-3 h-4 w-4 border-[3px]" />
        )}
      </PopoverContent>
    </Popover>
  );
}

function ProjectAndStageCombobox() {
  const [open, setOpen] = useState(false);
  const [stageOpen, setStageOpen] = useState(false);
  const newEntryCtx = useNewEntry();
  const { data: projects } = useGetProjects();
  return (
    <div className="flex">
      <Popover open={open} onOpenChange={setOpen} modal={true}>
        <PopoverTrigger asChild>
          <Button
            variant="secondary"
            role="combobox"
            aria-expanded={open}
            className={clsx(
              "group relative truncate font-normal",
              !!newEntryCtx.state.project?.stages.filter((s) => !s.isUnassigned)
                .length && "rounded-r-none border-r-0"
            )}
          >
            <RocketLaunchIcon
              className={clsx(
                "3.5 mr-2 w-3.5 shrink-0 text-gray-600"
                // newEntryCtx.state.project ? "text-gray-600" : "text-gray-400"
              )}
            />
            {newEntryCtx.state.project ? (
              <div className="max-w-[160px] truncate">
                {newEntryCtx.state.project.name}
              </div>
            ) : (
              <div className="text-gray-400/60">
                Project
                <span className="top-0 ml-0.5 text-primary-main">*</span>
              </div>
            )}
            {/* {!!selectedProject && (
              <div className="group absolute right-0 h-full w-20 bg-gradient-to-r from-transparent via-gray-50/70 to-gray-100 opacity-0 transition-all group-hover:opacity-100">
                <div
                  className="group absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-gray-200 p-[3px] opacity-0 transition-all group-hover:opacity-100"
                  onClick={(e) => {
                    e.stopPropagation();
                    onProjectDelete();
                  }}
                >
                  <XMarkIcon className="h-2.5 w-2.5 text-gray-400 group-hover:text-gray-600" />
                </div>
              </div>
            )} */}
            {/* <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" /> */}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          animateOut={false}
          className="pointer-events-auto w-60 p-0"
          align="start"
        >
          {!!projects ? (
            <>
              {projects?.length > 0 ? (
                <Command
                  filter={(value, search) => {
                    if (
                      projects
                        ?.find((p) => p._id === value)
                        ?.name.toLowerCase()
                        .includes(search.toLowerCase())
                    ) {
                      return 1;
                    } else {
                      return 0;
                    }
                  }}
                >
                  <CommandInput
                    placeholder="Search project..."
                    className="h-9"
                  />
                  <CommandList>
                    <CommandEmpty>No project found.</CommandEmpty>
                    <CommandGroup>
                      {projects?.map((project) => (
                        <CommandItem
                          key={project._id}
                          value={project._id}
                          onSelect={() => {
                            newEntryCtx.dispatch({
                              type: "PROJECT_UPDATE",
                              project,
                            });
                            newEntryCtx.dispatch({
                              type: "STAGE_UPDATE",
                              stage: undefined,
                            });
                            newEntryCtx.dispatch({
                              type: "TASK_UPDATE",
                              task: undefined,
                            });
                            setOpen(false);
                          }}
                          className="gap-2"
                        >
                          <span className="truncate">{project.name}</span>
                          <div className="flex flex-1 shrink-0 items-center justify-end space-x-1">
                            {newEntryCtx.state.project?._id === project._id ? (
                              <div
                                className="cursor-pointer rounded-full p-1 transition-all hover:bg-gray-200"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  newEntryCtx.dispatch({
                                    type: "PROJECT_UPDATE",
                                    project: undefined,
                                  });
                                  setOpen(false);
                                }}
                              >
                                <XMarkIcon
                                  className={clsx(
                                    "h-2.5 w-2.5 shrink-0",
                                    newEntryCtx.state.project?._id ===
                                      project._id
                                      ? "opacity-100"
                                      : "opacity-0"
                                  )}
                                />
                              </div>
                            ) : (
                              <div className="w-5" />
                            )}
                            <CheckIcon
                              className={clsx(
                                "h-4 w-4 shrink-0",
                                newEntryCtx.state.project?._id === project._id
                                  ? "opacity-100"
                                  : "opacity-0"
                              )}
                            />
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              ) : (
                <div className="px-4 py-2.5 text-sm">
                  <div className="select-none">No Projects Created</div>
                  {/* <div className="select-none pt-0.5 text-xs text-gray-500">
                    Click{" "}
                    <span className="cursor-pointer text-gray-700 hover:text-gray-800">
                      here
                    </span>{" "}
                    to create a project
                  </div> */}
                </div>
              )}
            </>
          ) : (
            <Loading className="mx-auto my-3 h-4 w-4 border-[3px]" />
          )}
        </PopoverContent>
      </Popover>
      {!!newEntryCtx.state.project?.stages.filter((s) => !s.isUnassigned)
        .length && (
        <Popover open={stageOpen} onOpenChange={setStageOpen} modal={true}>
          <PopoverTrigger asChild>
            <Button
              variant="secondary"
              role="combobox"
              aria-expanded={open}
              className="truncate rounded-l-none font-normal"
            >
              <SquareIcon
                className={clsx(
                  "mr-2 h-3.5 w-3.5 shrink-0 text-gray-600"
                  // !!newEntryCtx.state.stage ? "text-gray-600" : "text-gray-400"
                )}
              />
              {!!newEntryCtx.state.stage &&
              !newEntryCtx.state.stage.isUnassigned ? (
                <div className="max-w-[100px] truncate">
                  {newEntryCtx.state.stage.name}
                </div>
              ) : (
                <div className="text-gray-400/60">Stage</div>
              )}
              {/* <CaretSortIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" /> */}
            </Button>
          </PopoverTrigger>
          <PopoverContent
            animateOut={false}
            className="pointer-events-auto w-fit min-w-[200px] max-w-[480px] p-0"
            align="start"
          >
            <Command
              filter={(value, search) => {
                if (
                  newEntryCtx.state.project?.stages
                    .filter((s) => !s.isUnassigned)
                    .find((s) => s._id === value)
                    ?.name.toLowerCase()
                    .includes(search.toLowerCase())
                ) {
                  return 1;
                } else {
                  return 0;
                }
              }}
            >
              <CommandInput placeholder="Search stage..." className="h-9" />
              <CommandList>
                <CommandEmpty>No stage found.</CommandEmpty>
                <CommandGroup>
                  {newEntryCtx.state.project?.stages
                    .filter((s) => !s.isUnassigned)
                    .map((stage) => (
                      <CommandItem
                        key={stage._id}
                        value={stage._id}
                        onSelect={() => {
                          newEntryCtx.dispatch({
                            type: "STAGE_UPDATE",
                            stage,
                          });
                          if (!!newEntryCtx.state.task?._id) {
                            const taskStageId =
                              newEntryCtx.state.task?.journeyStage?._id;
                            if (taskStageId !== stage._id) {
                              newEntryCtx.dispatch({
                                type: "TASK_UPDATE",
                                task: undefined,
                              });
                            }
                          }

                          setStageOpen(false);
                        }}
                        className="gap-2"
                      >
                        <span className="truncate">{stage.name}</span>
                        <div className="flex flex-1 shrink-0 items-center justify-end space-x-1">
                          {newEntryCtx.state.stage?._id === stage._id ? (
                            <div
                              className="cursor-pointer rounded-full p-1 transition-all hover:bg-gray-200"
                              onClick={(e) => {
                                e.stopPropagation();
                                newEntryCtx.dispatch({
                                  type: "STAGE_UPDATE",
                                  stage: undefined,
                                });
                                setStageOpen(false);
                              }}
                            >
                              <XMarkIcon
                                className={clsx(
                                  "h-2.5 w-2.5 shrink-0",
                                  newEntryCtx.state.stage?._id === stage._id
                                    ? "opacity-100"
                                    : "opacity-0"
                                )}
                              />
                            </div>
                          ) : (
                            <div className="w-5" />
                          )}
                          <CheckIcon
                            className={clsx(
                              "ml-auto h-4 w-4 shrink-0",
                              newEntryCtx.state.stage?._id === stage._id
                                ? "opacity-100"
                                : "opacity-0"
                            )}
                          />
                        </div>
                      </CommandItem>
                    ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}

// Context for project and stage

type Action =
  | {
      type: "PROJECT_UPDATE";
      project: ProjectData | undefined;
    }
  | {
      type: "STAGE_UPDATE";
      stage: ProjectData["stages"][0] | undefined;
    }
  | {
      type: "TASK_UPDATE";
      task: TaskData | undefined;
    }
  | {
      type: "TIME_UPDATE";
      time: number | null;
    }
  | {
      type: "DESCRIPTION_UPDATE";
      description: string;
    }
  | {
      type: "DATE_UPDATE";
      date: string;
    }
  | {
      type: "BILLABLE_UPDATE";
      isBillable: boolean;
    }
  | {
      type: "IS_CREATE_MORE_UPDATE";
      isCreateMore: boolean;
    };

type State = {
  project: ProjectData | undefined;
  stage: ProjectData["stages"][0] | undefined;
  task: TaskData | undefined;
  time: number | null;
  description: string;
  date: string;
  isBillable: boolean;
  isCreateMore: boolean;
};

const NewEntryContext = React.createContext<
  | {
      state: State;
      dispatch: React.Dispatch<Action>;
    }
  | undefined
>(undefined);

function newEntryReducer(state: State, action: Action) {
  switch (action.type) {
    case "PROJECT_UPDATE":
      return { ...state, project: action.project };
    case "STAGE_UPDATE":
      return { ...state, stage: action.stage };
    case "TASK_UPDATE":
      return { ...state, task: action.task };
    case "TIME_UPDATE":
      return { ...state, time: action.time };
    case "DESCRIPTION_UPDATE":
      return { ...state, description: action.description };
    case "DATE_UPDATE":
      return { ...state, date: action.date };
    case "BILLABLE_UPDATE":
      return { ...state, isBillable: action.isBillable };
    case "IS_CREATE_MORE_UPDATE":
      return { ...state, isCreateMore: action.isCreateMore };
    default:
      return state;
  }
}

export function NewEntryContextProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [state, dispatch] = React.useReducer(newEntryReducer, {
    project: undefined,
    stage: undefined,
    task: undefined,
    time: null,
    description: "",
    date: dayjs().format("YYYY-MM-DD"),
    isBillable: true,
    isCreateMore: false,
  });
  const value = { state, dispatch };
  return (
    <NewEntryContext.Provider value={value}>
      {children}
    </NewEntryContext.Provider>
  );
}

export function useNewEntry() {
  const context = React.useContext(NewEntryContext);
  if (context === undefined) {
    throw new Error(
      "useNewEntry must be used within a NewEntryContextProvider"
    );
  }
  return context;
}
